import toast from 'react-hot-toast';
import {
  AlarmTemplate,
  NotificationTemplate,
} from './ui/ToastTemplate/NotificationTemplate';
import type { NotificationSocket } from '@/types/user/notification-types';
import type { AlarmsSocket } from '@/types/user/alarm-types';
import type { UnknownAction } from '@reduxjs/toolkit';
import { useSharedWebSocket } from '@/hooks/socket/useSharedWebSocket';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useEffect, useRef } from 'react';
import { commonActions } from '@/store/slices/commonSlice';

const wsReportsUrl = import.meta.env.VITE_WS_REPORTS_URL;
export const NonCustomerConfig = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.authUserData.data);

  const muteAlarmSound = user?.tenant?.settings?.mute_alarm_sound;
  const muteTaskSound = user?.tenant?.settings?.mute_task_sound;
  const muteNotifSound = user?.tenant?.settings?.mute_notification_sound;

  const notif_audioRef = useRef<HTMLAudioElement | null>(null);
  const alarm_audioRef = useRef<HTMLAudioElement | null>(null);
  const tasks_audioRef = useRef<HTMLAudioElement | null>(null);

  const notif_lastPlayedRef = useRef(0);
  const alarm_lastPlayedRef = useRef(0);
  const tasks_lastPlayedRef = useRef(0);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    // preload audio
    notif_audioRef.current = new Audio('/assets/sounds/notification.wav');
    alarm_audioRef.current = new Audio('/assets/sounds/alarm.mp3');
    tasks_audioRef.current = new Audio('/assets/sounds/task.wav');

    notif_audioRef.current.volume = 0.5;
    alarm_audioRef.current.volume = 1;
    tasks_audioRef.current.volume = 0.5;

    // unlock autoplay
    const unlockAudio = async () => {
      const audios = [
        notif_audioRef.current,
        alarm_audioRef.current,
        tasks_audioRef.current,
      ];

      for (const audio of audios) {
        if (!audio) continue;

        try {
          await audio?.play();
          audio?.pause();
          audio.currentTime = 0;
        } catch (err) {
          console.error('Unlock failed:', audio?.src, err);
        }
      }

      window.removeEventListener('pointerdown', unlockAudio);
    };

    window.addEventListener('pointerdown', unlockAudio);

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
    };
  }, []);

  useSharedWebSocket(`${wsReportsUrl}/ws/connect`);
  const { rawMessage, readyState } = useSharedWebSocket(
    `${wsReportsUrl}/ws/notifications`,
  );
  const { rawMessage: rMAlarms, readyState: rSAlarms } = useSharedWebSocket(
    `${wsReportsUrl}/ws/alarms`,
  );

  useEffect(() => {
    const _rawMessage = rMAlarms as AlarmsSocket;

    if (readyState && _rawMessage?.alarm_id) {
      const now = Date.now();
      const COOLDOWN = 1500;

      let action: ((bool: boolean) => UnknownAction) | undefined = undefined;
      action = (bool: boolean) => commonActions.setNewAlarmIncoming(bool);

      if (!muteAlarmSound && now - alarm_lastPlayedRef.current > COOLDOWN) {
        alarm_audioRef.current
          ?.play()
          .then(() => {
            if (alarm_audioRef.current) alarm_audioRef.current.currentTime = 0;
          })
          .catch(() => {});

        alarm_lastPlayedRef.current = now;
      }

      if (action) {
        dispatch(action(true));
        setTimeout(() => {
          dispatch(action(false));
        }, 2000);
      }

      const icon = '/assets/images/desktop-notif/alarm-line-icon.jpg';

      try {
        if (Notification.permission === 'granted') {
          new Notification((rMAlarms as AlarmsSocket).message, {
            body: (rMAlarms as AlarmsSocket).message,
            icon,
          });
        }
      } catch (error) {
        console.error(error);
      }

      toast.custom(
        (t) => (
          <AlarmTemplate
            isToastVisible={t.visible}
            dismiss={() => toast.dismiss(t.id)}
            notification={rMAlarms as AlarmsSocket}
          />
        ),
        {
          duration: Infinity,
        },
      );
    }
  }, [rMAlarms, rSAlarms]);

  useEffect(() => {
    const _rawMessage = rawMessage as NotificationSocket;

    if (readyState && _rawMessage?.msg_id) {
      const now = Date.now();
      const COOLDOWN = 1500;

      let action: ((bool: boolean) => UnknownAction) | undefined = undefined;
      switch (_rawMessage.msg_category?.toLowerCase()) {
        case 'task': {
          action = (bool: boolean) => commonActions.setNewTaskAdded(bool);
          if (muteTaskSound) break;
          if (now - tasks_lastPlayedRef.current > COOLDOWN) {
            tasks_audioRef.current
              ?.play()
              .then(() => {
                if (tasks_audioRef.current)
                  tasks_audioRef.current.currentTime = 0;
              })
              .catch((err) => {
                console.error('error', err);
              });
            tasks_lastPlayedRef.current = now;
          }
          break;
        }
        case 'alarm': {
          action = (bool: boolean) => commonActions.setNewAlarmIncoming(bool);
          if (muteAlarmSound) break;
          if (now - alarm_lastPlayedRef.current > COOLDOWN) {
            alarm_audioRef.current
              ?.play()
              .then(() => {
                if (alarm_audioRef.current)
                  alarm_audioRef.current.currentTime = 0;
              })
              .catch(() => {});

            alarm_lastPlayedRef.current = now;
          }
          break;
        }
        case 'notification': {
          action = (bool: boolean) =>
            commonActions.setNewNotificationIncoming(bool);
          if (muteNotifSound) break;
          if (now - notif_lastPlayedRef.current > COOLDOWN) {
            notif_audioRef.current
              ?.play()
              .then(() => {
                if (notif_audioRef.current)
                  notif_audioRef.current.currentTime = 0;
              })
              .catch(() => {});
            notif_lastPlayedRef.current = now;
          }
          break;
        }
      }

      if (action) {
        dispatch(action(true));
        setTimeout(() => {
          dispatch(action(false));
        }, 2000);
      }

      const bellImg = '/assets/images/desktop-notif/bell-icon.jpg';
      const alarmImg = '/assets/images/desktop-notif/alarm-line-icon.jpg';
      const taskImg = '/assets/images/desktop-notif/spanner.jpg';
      let icon = '';
      switch (_rawMessage.msg_category?.toLowerCase()) {
        case 'task': {
          icon = taskImg;
          break;
        }
        case 'alarm': {
          icon = alarmImg;
          break;
        }
        case 'notification': {
          icon = bellImg;
          break;
        }
      }
      try {
        if (Notification.permission === 'granted') {
          new Notification((rawMessage as NotificationSocket).msg, {
            body: (rawMessage as NotificationSocket).msg,
            icon,
          });
        }
      } catch (error) {
        console.error(error);
      }

      toast.custom((t) => (
        <NotificationTemplate
          isToastVisible={t.visible}
          dismiss={() => toast.dismiss(t.id)}
          notification={rawMessage}
        />
      ));
    }
  }, [rawMessage, readyState]);

  return <>{children}</>;
};
